
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Release } from "@/hooks/useReleases";
import { ExternalLink, Calendar } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { format } from "date-fns";
import { pl, enUS } from "date-fns/locale";
import { useState } from "react";

interface ReleaseCardProps {
  release: Release;
}

export function ReleaseCard({ release }: ReleaseCardProps) {
  const { language, t } = useLanguage();
  const locale = language === "pl" ? pl : enUS;
  const [imageError, setImageError] = useState(false);
  
  // Format the date based on language
  const formattedDate = format(
    new Date(release.data_wydania),
    "PPP",
    { locale }
  );

  // Enhanced function to optimize the image URL for better performance
  const getOptimizedImageUrl = (url: string) => {
    if (!url) return '';
    
    // GitHub avatar optimization with small size
    if (url.includes('github.com') || url.includes('githubusercontent.com')) {
      return `${url}?size=40`;
    } else if (url.includes('avatars.githubusercontent.com')) {
      return url.includes('?') ? `${url}&s=40` : `${url}?s=40`;
    }
    
    // Add query parameters for other image hosts if they support it
    if (url.includes('cloudinary.com')) {
      return url.replace('/image/upload/', '/image/upload/w_40,h_40,c_fill/');
    }
    
    // For other URLs, just return as is
    return url;
  };

  return (
    <Card className="hover:shadow-md transition-shadow duration-300">
      <CardHeader className="pb-2 pt-4 px-4 flex flex-row items-center space-y-0 gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage 
            src={getOptimizedImageUrl(release.url_zdjecia)} 
            alt={release.nazwa_projektu}
            loading="lazy" 
            onError={() => setImageError(true)}
          />
          <AvatarFallback>{release.nazwa_projektu.substring(0, 2).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-base md:text-lg leading-none">
              {release.nazwa_projektu}
            </h3>
            <Badge variant="outline" className="font-mono text-xs">
              {release.wersja}
            </Badge>
          </div>
          <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3" />
            <span>{formattedDate}</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 pb-4 px-4">
        <a 
          href={release.url_projektu} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm text-primary hover:underline flex items-center gap-1 mt-2"
        >
          <ExternalLink className="h-3 w-3" />
          {t('releases.github_repository')}
        </a>
      </CardContent>
    </Card>
  );
}
